/* eslint-disable prettier/prettier */
import React from 'react'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showToast } from './toastSlice'
import makeRequest from 'src/services/api'
import handleGeneralError from '../helpers/errorHandler'
import {
  fetchBannersUrl,
  addBannerUrl,
  editBannerUrl,
  uploadBannerImageUrl,
  fetchStatesUrl,
} from 'src/services/urls'

const initialState = {
  loading: false,
  countryConfig: {},
  countryConfigError: '',
  data: { hasMore: false, page: 1, totalDocs: 0, nextPage: 0 },
  docs: [],
  states: [],
  error: '',
  filters: { businessUnit: 'CP', state: null },
  activePage: 0,
  pageSize: 10,
}

export const fetchBanners = createAsyncThunk(
  'bannerManagement/fetchBanners',
  async (data, { dispatch }) => {
    try {
      const body = {
        businessUnit: data?.businessUnit || 'CP',
        stateId: data?.state,
        limit: 1000,
      }
      const statesResponse = await makeRequest(fetchStatesUrl())
      const response = await makeRequest(fetchBannersUrl(body))

      return {
        docs: response.data.data.docs,
        data: response.data.data,
        states: statesResponse?.data?.data,
      }
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
      return { error: e?.response?.data?.error?.message }
    }
  },
)

export const addBanner = createAsyncThunk(
  'bannerManagement/addBanner',
  async (data, { dispatch, getState }) => {
    try {
      const { filters } = getState().bannerManagement
      const body = { ...data }

      const response = await makeRequest(addBannerUrl(), 'POST', body)

      const { displayMessage, status } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
        dispatch(fetchBanners(filters))
      }

      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const editBanner = createAsyncThunk(
  'bannerManagement/editBanner',
  async (data, { dispatch, getState }) => {
    try {
      const { filters } = getState().bannerManagement
      const body = { ...data }
      const response = await makeRequest(editBannerUrl(), 'PATCH', body)

      const { displayMessage, status } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
        dispatch(fetchBanners(filters))
      }

      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

export const uploadBannerImage = createAsyncThunk(
  'countries/uploadBannerImage',
  async (data, { dispatch, getState }) => {
    try {
      const { filters } = getState().bannerManagement
      const formData = new FormData()
      formData.append('id', data.id)
      formData.append('module', data.module)
      formData.append('file', data.file)

      const response = await makeRequest(uploadBannerImageUrl(), 'POST', formData)
      const { displayMessage, status } = response.data

      if (response.data.statusCode === 200) {
        dispatch(showToast({ title: status, message: displayMessage, color: 'success' }))
        setTimeout(() => {
          dispatch(fetchBanners(filters))
        }, 300)
      }
      return response.data
    } catch (e) {
      handleGeneralError(e, { dispatch, showToast })
    }
  },
)

const bannerManagementSlice = createSlice({
  name: 'bannerManagement',
  initialState,
  reducers: {
    resetDocs: (state, action) => {
      state.docs = []
      state.data = {}
    },
    setActivePage: (state, action) => {
      state.activePage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setFilters: (state, action) => {
      state.filters = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBanners.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchBanners.fulfilled, (state, action) => {
      state.loading = false
      state.error = action.payload.error
      if (!action.payload.error) {
        state.data = action.payload.data
        state.docs = action.payload.docs
        state.states = action.payload?.states
      }
    })
    builder.addCase(fetchBanners.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.docs = []
      state.error = action.error.message
    })
    builder.addCase(addBanner.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(addBanner.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.error = action.error.message
    })
    builder.addCase(editBanner.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(editBanner.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.error = action.error.message
    })
    builder.addCase(uploadBannerImage.fulfilled, (state, action) => {
      state.loading = false
      state.error = ''
    })
    builder.addCase(uploadBannerImage.rejected, (state, action) => {
      state.loading = false
      state.data = {}
      state.error = action.error.message
    })
  },
})

export default bannerManagementSlice.reducer
export const { setActivePage, setFilters, setPageSize, resetDocs } = bannerManagementSlice.actions
